import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Beaker, RotateCcw } from 'lucide-react';
import { useTesterModality, TESTER_MODALITIES } from '../context/TesterModalityContext';

const COLOR_CLS = {
    pink: 'bg-pink-600 hover:bg-pink-700 text-white',
    indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    sky: 'bg-sky-600 hover:bg-sky-700 text-white',
    emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white',
};

const OUTLINE_CLS = {
    pink: 'border-pink-300 text-pink-800 hover:bg-pink-50',
    indigo: 'border-indigo-300 text-indigo-800 hover:bg-indigo-50',
    sky: 'border-sky-300 text-sky-800 hover:bg-sky-50',
    emerald: 'border-emerald-300 text-emerald-800 hover:bg-emerald-50',
};

/**
 * Selector visible SÓLO para cuentas testers multi-modalidad (whitelist backend).
 * No cambia perfil.nivel; sólo cambia la "modalidad activa de prueba" (localStorage
 * + header X-Tester-Modality). El aislamiento (user_id, modalidad) lo mantiene el
 * backend en /api/drafts create/list y /api/chat/analyze.
 */
export default function TesterModalitySwitcher() {
    const { isTester, activeModality, setActiveModality, clearModality } = useTesterModality();
    if (!isTester) return null;

    const active = TESTER_MODALITIES.find((m) => m.id === activeModality);

    return (
        <Card className="border-2 border-dashed border-slate-300 bg-slate-50/70" data-testid="tester-modality-switcher">
            <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                        <Beaker className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="bg-slate-900 text-white text-[10px] uppercase tracking-wider">Modo QA</Badge>
                            <h3 className="font-heading text-sm font-bold text-slate-900">Cambiar modalidad de prueba</h3>
                            {active && (
                                <Badge className={`text-[10px] uppercase tracking-wider ${COLOR_CLS[active.color]}`}>
                                    Activa: {active.label}
                                </Badge>
                            )}
                        </div>
                        <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                            Alterna entre las cuatro modalidades sin alterar tu modalidad registrada.
                            Los borradores y progreso quedan aislados por <code className="rounded bg-slate-200 px-1 text-[10px]">user_id + modalidad</code>.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {TESTER_MODALITIES.map((m) => {
                                const isActive = m.id === activeModality;
                                return (
                                    <Button
                                        key={m.id}
                                        size="sm"
                                        className={`gap-1 text-xs ${isActive ? COLOR_CLS[m.color] : 'bg-white border ' + OUTLINE_CLS[m.color]}`}
                                        variant={isActive ? 'default' : 'outline'}
                                        onClick={() => setActiveModality(m.id)}
                                        data-testid={`tester-modality-${m.id}`}
                                    >
                                        {m.label}
                                    </Button>
                                );
                            })}
                            {activeModality && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="gap-1 text-xs text-slate-600"
                                    onClick={clearModality}
                                    data-testid="tester-modality-clear"
                                >
                                    <RotateCcw className="h-3 w-3" />
                                    Volver a mi modalidad real
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
